import React, { useEffect } from 'react';
import { Popover } from '@barges/react';
import { analyticsService } from '@common/analytics';
import { changeAppStateAPI } from '@common/api/api';
import { usePurchaseFlowRenderProps } from '@common/config/purchaseFlowRenderHelpers';
import SelectChatCredit from './select_chat_credit';
import NotInLiveMode from './not_in_live_mode';
import CantCreate from './cant_create';
import CreatedChat from '../created_chat';
import GeneralError from './general_error';
import Constants from '../../config/const';
import IntroComponent from '../intro';
import { PurchaseFlowWrapper } from '../purchase_flow';
import NoAnswer from './no_answer';
import classes from './classes.module.scss';
import { log, LOG_LEVELS } from '../../../common/config/app_logger';
import { hideBodyScroll, unlockBodyScroll } from '../../../common/config/utils';
import { useZendeskMessaging } from '../zendesk_messaging_provider';

function ChatOverlay({
  state,
  purchaseOnBack,
  purchaseSuccess,
  cancelChat,
  orderId
}) {
  const { renderIcon, renderText } = usePurchaseFlowRenderProps();
  const { setHidden } = useZendeskMessaging();
  let content = null;

  useEffect(() => {
    setHidden(true);
    return () => setHidden(false);
  }, [setHidden]);

  useEffect(() => {
    const originalBeforeUnload = window.onbeforeunload;
    window.onbeforeunload = () => {
      log(LOG_LEVELS.INFO, 'Chat', 'Page is refreshed');
      return 'Test message';
    };

    const handlePageHide = () => {
      changeAppStateAPI(orderId, 'closed_window');
    };
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.onbeforeunload = originalBeforeUnload;
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [orderId]);

  useEffect(() => {
    hideBodyScroll();
    return () => {
      unlockBodyScroll();
    };
  }, [state]);

  switch (state) {
    case Constants.chatStates.paymentRequired:
      log(LOG_LEVELS.INFO, 'Chat', 'state paymentRequired');
      content = (
        <PurchaseFlowWrapper
          mode="additionalFunds"
          onComplete={ purchaseSuccess }
          onCancel={ purchaseOnBack }
          onAnalyticsEvent={ (...args) => analyticsService.trackEvent(...args) }
          renderIcon={ renderIcon }
          renderText={ renderText }
          inModal
          popoverContentClassName={ classes.purchaseDetailsPopupContent }
          // Legacy pass-through props
          chatType={ Constants.chatType.text }
          backArrowAction={ purchaseOnBack }
        />
      );
      break;
    case Constants.chatStates.appInitial:
      log(LOG_LEVELS.INFO, 'Chat', 'state appInitial');
      content = (
        <Popover isOpen className="appInitialPopupContainer">
          <SelectChatCredit
            chatType={ Constants.chatType.text }
            selectorOpenedTime={ new Date() }
          />
        </Popover>
      );
      break;
    case Constants.chatStates.introduce:
      log(LOG_LEVELS.INFO, 'Chat', 'state introduce');
      content = (
        <IntroComponent chatType={ Constants.chatType.text } closeButtonAction={ cancelChat } />
      );
      break;
    case Constants.chatStates.appNotInLiveMode:
      log(LOG_LEVELS.INFO, 'Chat', 'state appNotInLiveMode');
      content = (
        <div className={ classes.callingScreen }>
          <NotInLiveMode chatType={ Constants.chatType.text } />
        </div>
      );
      break;
    case Constants.chatStates.appCantCreate:
      log(LOG_LEVELS.INFO, 'Chat', 'state appCantCreate');
      content = (
        <div className={ classes.callingScreen }>
          <CantCreate chatType={ Constants.chatType.text } />
        </div>
      );
      break;
    case Constants.chatStates.generalError:
      log(LOG_LEVELS.ERROR, 'Chat', 'state generalError');
      content = (
        <div className={ classes.callingScreen }>
          <GeneralError chatType={ Constants.chatType.text } />
        </div>
      );
      break;
    case Constants.chatStates.noAnswer:
      log(LOG_LEVELS.INFO, 'Chat', 'state noAnswer');
      content = (
        <div className={ classes.callingScreen }>
          <NoAnswer chatType={ Constants.chatType.text } />
        </div>
      );
      break;
    default:
      // once we have an id, it means that CreatedChat will take care of the rendering
      log(LOG_LEVELS.INFO, 'Chat', 'created');
      content = <CreatedChat />;
      break;
  }

  return (
    <div className={ classes.layout }>
      {content}
    </div>
  );
}

export default ChatOverlay;
